
import React, { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Icon } from '@/components/ui/icon'
import type { Canvas } from '@/hooks/useCanvas'

interface CanvasDeleteModalProps {
  isOpen: boolean
  onClose: () => void
  canvas: Canvas | null
  onDelete: (canvasId: string) => Promise<void>
}

export const CanvasDeleteModal: React.FC<CanvasDeleteModalProps> = ({
  isOpen,
  onClose,
  canvas,
  onDelete,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleDelete = async () => {
    if (!canvas) return

    setIsLoading(true)
    try {
      await onDelete(canvas.uuid)
      onClose()
    } catch (error) {
      console.error('Failed to delete canvas:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Session</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete &quot;{canvas?.title}&quot;? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? (
              <>
                <Icon name="loader-2" className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}